import React, { useEffect, useState } from "react";
import Axios from "axios";

import sampleApps from "config/sample-apps";
import deploymentStore from "store/deployments/deployments.ts";
import { SlashZoneSelect } from "../../shared/SlashZoneSelect/SlashZoneSelect";
import api from "libs/api";
import { useAuth } from "libs/auth";
import config from "config";
import LoadingSpinner from "components/shared/LoadingSpinner/LoadingSpinner";
import { useHistory, withRouter } from "react-router-dom";
import { useAppRoute } from "config";
import { updateSchema } from "libs/schema";
import trackEvent, { events, trackPage } from "libs/trackevents";
import {
    Stack,
    Card,
    Heading,
    Text,
    Container,
    Modal,
} from "@dgraph-io/typhoon-ui";
import { PageContentContainerStyles } from "../styles";
import { styles } from "./AppStore.styles";
import SampleApp from "./SampleApp";
import SampleAppDetail from "./SampleAppDetail";
import { ReactComponent as FeatureCard } from "./icons/featured_card.svg";
import { useCloudProviders } from "hooks/useCloudProviders/useCloudProviders";
import { useToast } from "@dgraph-io/typhoon-ui";
import { ToastContext } from "libs/toast.ts";

const SPLIT_SCHEMA = "------SCHEMA AUTH------";
const CHECK_HEALTH_FREQUENCY = 5 * 1000;

function random_item(items) {
    var randIndex =
        window.crypto.getRandomValues(new Uint32Array(1))[0] % items.length;
    return items[randIndex];
}

const NAMES = [
    "furious-tigress",
    "furious-mantis",
    "furious-viper",
    "furious-panda",
    "furious-monkey",
    "furious-crane",
];
const EMAILS = ["manish@dgraph.io", "tejas@dgraph.io", "sankalan@dgraph.io"];
const createDeployment = async (app, zone, { email }, toast) => {
    try {
        const createDeploymentInput = {
            name: app.name,
            zone,
        };
        // FIXME: Hack for demo and conference. When certain people create
        // deployments, it picks a well known name so that it's fast
        if (app.key === "surveyo" && EMAILS.includes(email)) {
            createDeploymentInput.subdomain = random_item(NAMES);
        }
        const deployment = await deploymentStore.create({
            createDeploymentInput,
        });
        return deployment;
    } catch (e) {
        toast.addToast({
            description: "There was an error creating the deployment.",
            variant: "error",
        });
    }
};

const loadSampleAssets = async (app, toast) => {
    try {
        let res = await Axios.get(app.schemaUrl);
        const [schema, schemaWithAuth] = res.data.split(SPLIT_SCHEMA);

        let mutations = null;
        if (app.mutationsUrl) {
            res = await Axios.get(app.mutationsUrl);
            mutations = res.data;
        }

        return { schema, schemaWithAuth, mutations };
    } catch (e) {
        toast.addToast({
            description: "There was an error fetching the app data.",
            variant: "error",
        });
    }
};

const waitForDeployment = ({ url }) => {
    return new Promise(resolve => {
        const interval = setInterval(async () => {
            try {
                await deploymentStore.getHealthInfo(
                    url,
                    CHECK_HEALTH_FREQUENCY
                );
                clearInterval(interval);
                resolve();
            } catch (e) {
                console.warn("Waiting for backend.");
            }
        }, CHECK_HEALTH_FREQUENCY);
    });
};

const updateSchemaWithError = async (deployment, schema, toast) => {
    try {
        await updateSchema(deployment, schema);
    } catch (err) {
        toast.addToast({
            description: "There was an error updating the schema.",
            variant: "error",
        });
        console.log(err);
    }
};

const runMutation = async ({ url }, mutation, toast) => {
    try {
        await Axios.post(`${config.httpProtocol}://${url}/graphql`, {
            query: mutation,
        });
    } catch (e) {
        toast.addToast({
            description: "There was an error adding data to the deployment.",
            variant: "error",
        });
    }
};

// FIXME: Move to backend. Just need to test first
const deployFrontend = async (
    { uid, subdomain },
    { frontendFolder, frontendRepo },
    toast
) => {
    try {
        const res = await api.post(`/deployments/${uid}/one-click-deploy`, {
            frontendFolder,
            subdomain,
            frontendRepo,
        });
        return res.data.frontendUrl;
    } catch (e) {
        console.log(e);

        toast.addToast({
            description: "There was an error in deploying frontend.",
            variant: "error",
        });
    }
    // TODO: wait 2 mins?
    /*
        await new Promise(resolve => setTimeout(resolve, 2 * 60 * 1000));
     */
};

function AppStore(props) {
    const history = useHistory();
    const { user } = useAuth();
    const { path } = useAppRoute();
    const [loading, setLoading] = useState(false);
    const { slashZones } = useCloudProviders();
    const [zone, setZone] = useState(null);
    const [selectedOneClickApp, setSelectedOneClickApp] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const toast = useToast(ToastContext);
    const featuredApp = sampleApps && sampleApps.find(item => item.featured);

    useEffect(trackPage, []);

    useEffect(() => {
        const query = new URLSearchParams(props.location.search);
        const app = query.get("app");

        const sampleApp =
            sampleApps && sampleApps.find(item => item.key === app);

        setSelectedOneClickApp(sampleApp);
    }, [props.location.search]);

    const selectApp = app => {
        setSelectedOneClickApp(app);
        history.push(path + `/one-click?app=${app.key}`);
    };

    const onBackClick = () => {
        setSelectedOneClickApp(null);
        history.push(path + `/one-click`);
    };

    const deploySampleApp = async app => {
        setLoading(true);
        setShowModal(false);

        const [deployment, assets] = await Promise.all([
            createDeployment(app, zone, user, toast),
            loadSampleAssets(app, toast),
        ]);

        if (!deployment) {
            setLoading(false);
            return;
        }

        const [frontendUrl] = await Promise.all([
            deployFrontend(deployment, app, toast),
            (async () => {
                await waitForDeployment(deployment);
                await updateSchemaWithError(deployment, assets.schema, toast);
                if (assets.mutations) {
                    await runMutation(deployment, assets.mutations, toast);
                }
                if (assets.schemaWithAuth) {
                    await updateSchemaWithError(
                        deployment,
                        assets.schemaWithAuth
                    );
                }
            })(),
        ]);

        setLoading(false);

        deploymentStore.setCurrent(deployment);
        deploymentStore.setDeployed(deployment.uid, {
            frontendUrl,
            frontendRepo: app.frontendRepo,
        });
        trackEvent(events.oneClickDeploy, { name: app.name });
        history.push(path + "/dashboard");
    };

    // TODO: Show loading while deploying
    return (
        <Container styles={PageContentContainerStyles}>
            <LoadingSpinner
                loading={loading}
                text="We're spinning up your app now..."
            >
                {!selectedOneClickApp && (
                    <>
                        <Card styles={styles.AppStoreCardStyles}>
                            <Stack
                                direction="horizontal"
                                justify="space-between"
                                align="center"
                            >
                                <Stack
                                    direction="vertical"
                                    gap={3}
                                    styles={styles.AppStoreTextStyles}
                                >
                                    <Heading variant={1} weight={300}>
                                        Welcome to <br /> App Store
                                    </Heading>

                                    <Text
                                        color="grey"
                                        variant={3}
                                        styles={{ width: "90%" }}
                                    >
                                        Here you’ll find sample GraphQL
                                        applications powered by Dgraph. Deploy
                                        with one click to see how it works.
                                    </Text>
                                </Stack>
                                <Stack
                                    justify="space-around"
                                    direction="vertical"
                                    styles={styles.FeaturedCardStyles}
                                    onClick={setSelectedOneClickApp.bind(
                                        null,
                                        featuredApp
                                    )}
                                >
                                    <FeatureCard />

                                    <Container
                                        styles={{
                                            position: "absolute",
                                            padding: 3,
                                        }}
                                    >
                                        <Text variant={2} color="white">
                                            FEATURED APP
                                        </Text>
                                        <Heading
                                            color="white"
                                            styles={{ marginTop: 2 }}
                                        >
                                            {featuredApp.name}{" "}
                                        </Heading>
                                    </Container>

                                    <Container
                                        styles={styles.VerticalLine}
                                    ></Container>
                                </Stack>
                            </Stack>
                        </Card>

                        <Heading
                            variant={3}
                            weight={900}
                            styles={{ marginY: 4 }}
                        >
                            Top Apps
                        </Heading>

                        <Stack
                            fullWidth
                            direction="horizontal"
                            gap={0}
                            styles={styles.TopAppsStyles}
                        >
                            {sampleApps.map((app, index) => (
                                <SampleApp
                                    key={index}
                                    app={app}
                                    onClick={() => selectApp(app)}
                                />
                            ))}
                        </Stack>
                    </>
                )}
                {selectedOneClickApp && (
                    <SampleAppDetail
                        app={selectedOneClickApp}
                        onClick={onBackClick}
                        show={setShowModal.bind(null, true)}
                    />
                )}
            </LoadingSpinner>

            <Modal
                isOpen={showModal}
                heading="Choose deployment region"
                onClose={setShowModal.bind(null, false)}
                onContinue={() => deploySampleApp(selectedOneClickApp)}
                continueLabel="Deploy"
                disableContinueButton={zone === null}
            >
                {slashZones && (
                    <SlashZoneSelect
                        selected={zone}
                        onChange={zone => setZone(zone.zoneID)}
                    />
                )}
            </Modal>
        </Container>
    );
}

AppStore.propTypes = {};

export default withRouter(AppStore);
